<script setup lang="ts">
import type { CSSProperties } from 'vue'
import { normalizeBio } from '~/utils/bio'

const props = withDefaults(defineProps<{
  modelValue: string | null
  displayStyle?: CSSProperties
  richTextEnabled?: boolean
}>(), {
  displayStyle: () => ({}),
  richTextEnabled: true
})

const { requirePlan } = usePlanGate()

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const editor = useEditor({
  content: normalizeBio(props.modelValue),
  extensions: [TiptapStarterKit],
  onUpdate: ({ editor: ed }) => {
    emit('update:modelValue', ed.isEmpty ? '' : ed.getHTML())
  }
})

// Sync external value changes (e.g. after save/refresh)
watch(() => props.modelValue, (newVal) => {
  if (!editor.value) return
  const normalized = normalizeBio(newVal)
  if (editor.value.getHTML() !== normalized) {
    editor.value.commands.setContent(normalized, { emitUpdate: false })
  }
})

onBeforeUnmount(() => {
  unref(editor)?.destroy()
})
</script>

<template>
  <div class="tiptap-editor">
    <!-- Toolbar -->
    <div
      v-if="editor"
      class="relative flex flex-wrap items-center gap-0.5 mb-2 p-1 rounded-lg bg-elevated/50 border border-default"
    >
      <!-- Locked overlay for free plan -->
      <div
        v-if="!richTextEnabled"
        class="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-black/40 backdrop-blur-[1px] cursor-pointer"
        @click="requirePlan('Rich Text Bio', 'pro')"
      >
        <UBadge
          color="fuchsia"
          variant="solid"
          size="sm"
          class="gap-1"
        >
          <UIcon
            name="i-lucide-lock"
            class="size-3"
          />
          Pro
        </UBadge>
      </div>

      <!-- Text formatting -->
      <button
        title="Bold"
        class="toolbar-btn"
        :class="editor.isActive('bold') ? 'active' : 'inactive'"
        :disabled="!editor.can().chain().focus().toggleBold().run()"
        @click="editor.chain().focus().toggleBold().run()"
      >
        <UIcon
          name="i-lucide-bold"
          class="size-3.5"
        />
      </button>
      <button
        title="Italic"
        class="toolbar-btn"
        :class="editor.isActive('italic') ? 'active' : 'inactive'"
        :disabled="!editor.can().chain().focus().toggleItalic().run()"
        @click="editor.chain().focus().toggleItalic().run()"
      >
        <UIcon
          name="i-lucide-italic"
          class="size-3.5"
        />
      </button>
      <button
        title="Strikethrough"
        class="toolbar-btn"
        :class="editor.isActive('strike') ? 'active' : 'inactive'"
        :disabled="!editor.can().chain().focus().toggleStrike().run()"
        @click="editor.chain().focus().toggleStrike().run()"
      >
        <UIcon
          name="i-lucide-strikethrough"
          class="size-3.5"
        />
      </button>
      <button
        title="Inline Code"
        class="toolbar-btn"
        :class="editor.isActive('code') ? 'active' : 'inactive'"
        :disabled="!editor.can().chain().focus().toggleCode().run()"
        @click="editor.chain().focus().toggleCode().run()"
      >
        <UIcon
          name="i-lucide-code"
          class="size-3.5"
        />
      </button>

      <div class="toolbar-sep" />

      <!-- Clear formatting -->
      <button
        title="Clear Marks"
        class="toolbar-btn inactive"
        @click="editor.chain().focus().unsetAllMarks().run()"
      >
        <UIcon
          name="i-lucide-remove-formatting"
          class="size-3.5"
        />
      </button>
      <button
        title="Clear Nodes"
        class="toolbar-btn inactive"
        @click="editor.chain().focus().clearNodes().run()"
      >
        <UIcon
          name="i-lucide-eraser"
          class="size-3.5"
        />
      </button>

      <div class="toolbar-sep" />

      <!-- Block types -->
      <button
        title="Paragraph"
        class="toolbar-btn-text"
        :class="editor.isActive('paragraph') ? 'active' : 'inactive'"
        @click="editor.chain().focus().setParagraph().run()"
      >
        P
      </button>
      <button
        title="Heading 1"
        class="toolbar-btn-text"
        :class="editor.isActive('heading', { level: 1 }) ? 'active' : 'inactive'"
        @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
      >
        H1
      </button>
      <button
        title="Heading 2"
        class="toolbar-btn-text"
        :class="editor.isActive('heading', { level: 2 }) ? 'active' : 'inactive'"
        @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
      >
        H2
      </button>
      <button
        title="Heading 3"
        class="toolbar-btn-text"
        :class="editor.isActive('heading', { level: 3 }) ? 'active' : 'inactive'"
        @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
      >
        H3
      </button>
      <button
        title="Heading 4"
        class="toolbar-btn-text"
        :class="editor.isActive('heading', { level: 4 }) ? 'active' : 'inactive'"
        @click="editor.chain().focus().toggleHeading({ level: 4 }).run()"
      >
        H4
      </button>
      <button
        title="Heading 5"
        class="toolbar-btn-text"
        :class="editor.isActive('heading', { level: 5 }) ? 'active' : 'inactive'"
        @click="editor.chain().focus().toggleHeading({ level: 5 }).run()"
      >
        H5
      </button>
      <button
        title="Heading 6"
        class="toolbar-btn-text"
        :class="editor.isActive('heading', { level: 6 }) ? 'active' : 'inactive'"
        @click="editor.chain().focus().toggleHeading({ level: 6 }).run()"
      >
        H6
      </button>

      <div class="toolbar-sep" />

      <!-- Lists -->
      <button
        title="Bullet List"
        class="toolbar-btn"
        :class="editor.isActive('bulletList') ? 'active' : 'inactive'"
        @click="editor.chain().focus().toggleBulletList().run()"
      >
        <UIcon
          name="i-lucide-list"
          class="size-3.5"
        />
      </button>
      <button
        title="Ordered List"
        class="toolbar-btn"
        :class="editor.isActive('orderedList') ? 'active' : 'inactive'"
        @click="editor.chain().focus().toggleOrderedList().run()"
      >
        <UIcon
          name="i-lucide-list-ordered"
          class="size-3.5"
        />
      </button>

      <div class="toolbar-sep" />

      <!-- Block elements -->
      <button
        title="Code Block"
        class="toolbar-btn"
        :class="editor.isActive('codeBlock') ? 'active' : 'inactive'"
        @click="editor.chain().focus().toggleCodeBlock().run()"
      >
        <UIcon
          name="i-lucide-square-code"
          class="size-3.5"
        />
      </button>
      <button
        title="Blockquote"
        class="toolbar-btn"
        :class="editor.isActive('blockquote') ? 'active' : 'inactive'"
        @click="editor.chain().focus().toggleBlockquote().run()"
      >
        <UIcon
          name="i-lucide-text-quote"
          class="size-3.5"
        />
      </button>
      <button
        title="Horizontal Rule"
        class="toolbar-btn inactive"
        @click="editor.chain().focus().setHorizontalRule().run()"
      >
        <UIcon
          name="i-lucide-minus"
          class="size-3.5"
        />
      </button>
      <button
        title="Hard Break"
        class="toolbar-btn inactive"
        @click="editor.chain().focus().setHardBreak().run()"
      >
        <UIcon
          name="i-lucide-wrap-text"
          class="size-3.5"
        />
      </button>

      <div class="toolbar-sep" />

      <!-- History -->
      <button
        title="Undo"
        class="toolbar-btn inactive"
        :disabled="!editor.can().chain().focus().undo().run()"
        @click="editor.chain().focus().undo().run()"
      >
        <UIcon
          name="i-lucide-undo-2"
          class="size-3.5"
        />
      </button>
      <button
        title="Redo"
        class="toolbar-btn inactive"
        :disabled="!editor.can().chain().focus().redo().run()"
        @click="editor.chain().focus().redo().run()"
      >
        <UIcon
          name="i-lucide-redo-2"
          class="size-3.5"
        />
      </button>
    </div>

    <!-- Editor content -->
    <div
      class="tiptap-content rounded-lg border border-default p-3 min-h-20 transition-colors duration-200 focus-within:border-primary-400/30"
      :style="displayStyle"
    >
      <TiptapEditorContent :editor="editor" />
    </div>
  </div>
</template>

<style>
/* Toolbar button styles */
.toolbar-btn {
  padding: 6px;
  border-radius: 6px;
  cursor: pointer;
  transition: color 150ms, background-color 150ms;
}

.toolbar-btn-text {
  padding: 4px 6px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  transition: color 150ms, background-color 150ms;
}

.toolbar-btn.active,
.toolbar-btn-text.active {
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
}

.toolbar-btn.inactive,
.toolbar-btn-text.inactive {
  color: rgba(255, 255, 255, 0.4);
}

.toolbar-btn.inactive:hover,
.toolbar-btn-text.inactive:hover {
  color: rgba(255, 255, 255, 0.7);
  background-color: rgba(255, 255, 255, 0.05);
}

.toolbar-btn:disabled,
.toolbar-btn-text:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.toolbar-sep {
  width: 1px;
  height: 16px;
  background-color: rgba(255, 255, 255, 0.1);
  margin: 0 2px;
}

/* ProseMirror editor styles */
.tiptap-content .ProseMirror {
  outline: none;
  min-height: 60px;
}

.tiptap-content .ProseMirror > * + * {
  margin-top: 0.5em;
}

.tiptap-content .ProseMirror p {
  margin: 0;
}

.tiptap-content .ProseMirror p + p {
  margin-top: 0.25em;
}

.tiptap-content .ProseMirror h1,
.tiptap-content .ProseMirror h2,
.tiptap-content .ProseMirror h3,
.tiptap-content .ProseMirror h4,
.tiptap-content .ProseMirror h5,
.tiptap-content .ProseMirror h6 {
  font-weight: 700;
  line-height: 1.2;
}

.tiptap-content .ProseMirror h1 { font-size: 1.75em; }
.tiptap-content .ProseMirror h2 { font-size: 1.5em; }
.tiptap-content .ProseMirror h3 { font-size: 1.25em; }
.tiptap-content .ProseMirror h4 { font-size: 1.1em; }
.tiptap-content .ProseMirror h5 { font-size: 1em; }
.tiptap-content .ProseMirror h6 { font-size: 0.9em; }

.tiptap-content .ProseMirror ul,
.tiptap-content .ProseMirror ol {
  padding-left: 1.25em;
  margin: 0.25em 0;
}

.tiptap-content .ProseMirror li {
  margin: 0.1em 0;
}

.tiptap-content .ProseMirror blockquote {
  border-left: 3px solid rgba(255, 255, 255, 0.15);
  padding-left: 0.75em;
  margin: 0.5em 0;
  color: rgba(255, 255, 255, 0.6);
  font-style: italic;
}

.tiptap-content .ProseMirror code {
  background-color: rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  padding: 0.15em 0.3em;
  font-size: 0.9em;
}

.tiptap-content .ProseMirror pre {
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 0.75em 1em;
  margin: 0.5em 0;
  overflow-x: auto;
}

.tiptap-content .ProseMirror pre code {
  background: none;
  padding: 0;
  border-radius: 0;
  font-size: 0.85em;
}

.tiptap-content .ProseMirror hr {
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin: 0.75em 0;
}
</style>
